import React, { useState } from 'react';
import '../styles/landing.css';

const LandingPage = ({ onLogin }) => {
  const [showModal, setShowModal] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    setError('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');

    // Get API URL from environment variable, fallback to localhost for development
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

    try {
      const response = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          username: username.trim(),
          password: password
        }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        onLogin(username.trim());
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Failed to connect to server. Please make sure the backend is running.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <svg className="logo-icon" viewBox="0 0 40 40" fill="none">
              <path d="M5 20 Q 15 5, 25 20 T 35 20" stroke="#7FFF00" strokeWidth="3" fill="none"/>
              <path d="M8 25 L 32 25" stroke="#7FFF00" strokeWidth="2"/>
              <circle cx="20" cy="20" r="18" stroke="#7FFF00" strokeWidth="2" fill="none"/>
            </svg>
            <span className="logo-text">AEGIS</span>
          </div>
          <button className="header-login-btn" onClick={() => setShowModal(true)}>
            Get Started
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-dot"></span>
            AI-Powered Stroke Classification
          </div>
          <h1 className="hero-title">
            Capture the Art of
            <span className="highlight"> Industrial Design</span>
          </h1>
          <p className="hero-subtitle">
            A sophisticated platform for recording and classifying design strokes. 
            Build datasets that power the next generation of AI-driven design tools.
          </p>
          <div className="hero-buttons">
            <button className="btn-primary" onClick={() => setShowModal(true)}>
              Start Recording
              <svg className="btn-icon" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 3L17 10L10 17M17 10H3" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
            </button>
            <button className="btn-secondary" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}>
              Learn More
            </button>
          </div>

          {/* Animated Stroke Preview */}
          <div className="stroke-preview">
            <svg className="preview-svg" viewBox="0 0 600 400">
              <defs>
                <linearGradient id="strokeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#7FFF00" stopOpacity="0.2"/>
                  <stop offset="50%" stopColor="#7FFF00" stopOpacity="0.8"/>
                  <stop offset="100%" stopColor="#7FFF00" stopOpacity="0.2"/>
                </linearGradient>
              </defs>
              
              {/* Constraining Stroke */}
              <path className="stroke-anim stroke-1" d="M50 100 Q 150 50, 250 100" 
                    stroke="url(#strokeGradient)" strokeWidth="3" fill="none"/>
              
              {/* Defining Stroke */}
              <path className="stroke-anim stroke-2" d="M100 200 L 500 200 L 500 300 L 100 300 Z" 
                    stroke="#7FFF00" strokeWidth="2" fill="none"/>
              
              {/* Detailing Stroke */}
              <path className="stroke-anim stroke-3" d="M300 150 Q 350 120, 400 150 Q 450 180, 500 150" 
                    stroke="#7FFF00" strokeWidth="1.5" fill="none"/>
              
              {/* Hatches */}
              <g className="stroke-anim stroke-4">
                <line x1="120" y1="220" x2="140" y2="280" stroke="#7FFF00" strokeWidth="1"/>
                <line x1="140" y1="220" x2="160" y2="280" stroke="#7FFF00" strokeWidth="1"/>
                <line x1="160" y1="220" x2="180" y2="280" stroke="#7FFF00" strokeWidth="1"/>
                <line x1="180" y1="220" x2="200" y2="280" stroke="#7FFF00" strokeWidth="1"/>
              </g>
            </svg>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="floating-element float-1"></div>
        <div className="floating-element float-2"></div>
        <div className="floating-element float-3"></div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="features-content">
          <h2 className="section-title">Four Stroke Categories</h2>
          <p className="section-subtitle">
            Precisely classify every stroke type in industrial design sketching
          </p>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 50 50" fill="none">
                  <rect x="10" y="10" width="30" height="30" stroke="#7FFF00" strokeWidth="2" rx="4"/>
                  <path d="M15 25 L 35 25" stroke="#7FFF00" strokeWidth="2"/>
                </svg>
              </div>
              <h3>Constraining Strokes</h3>
              <p>Define boundaries and establish the overall proportions of your design concept</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 50 50" fill="none">
                  <path d="M10 25 Q 25 10, 40 25 Q 25 40, 10 25" stroke="#7FFF00" strokeWidth="2.5"/>
                </svg>
              </div>
              <h3>Defining Strokes</h3>
              <p>Create the primary forms and shapes that give your design its character</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 50 50" fill="none">
                  <circle cx="25" cy="25" r="15" stroke="#7FFF00" strokeWidth="2"/>
                  <path d="M20 25 Q 25 20, 30 25" stroke="#7FFF00" strokeWidth="1.5"/>
                  <circle cx="22" cy="22" r="2" fill="#7FFF00"/>
                </svg>
              </div>
              <h3>Detailing Strokes</h3>
              <p>Add refinement and intricate features to bring your concept to life</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 50 50" fill="none">
                  <line x1="15" y1="15" x2="20" y2="35" stroke="#7FFF00" strokeWidth="1"/>
                  <line x1="20" y1="15" x2="25" y2="35" stroke="#7FFF00" strokeWidth="1"/>
                  <line x1="25" y1="15" x2="30" y2="35" stroke="#7FFF00" strokeWidth="1"/>
                  <line x1="30" y1="15" x2="35" y2="35" stroke="#7FFF00" strokeWidth="1"/>
                </svg>
              </div>
              <h3>Hatches & Shadows</h3>
              <p>Apply shading and texture to communicate depth and materiality</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats">
        <div className="stats-content">
          <div className="stat-item">
            <div className="stat-number">4</div>
            <div className="stat-label">Stroke Types</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-number">AI</div>
            <div className="stat-label">Powered</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-number">∞</div>
            <div className="stat-label">Possibilities</div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="cta-content">
          <h2>Ready to Build the Future?</h2>
          <p>Join us in creating datasets that will train the next generation of AI design assistants</p>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            Get Started Now
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <svg className="logo-icon" viewBox="0 0 40 40" fill="none">
                  <path d="M5 20 Q 15 5, 25 20 T 35 20" stroke="#7FFF00" strokeWidth="3" fill="none"/>
                  <path d="M8 25 L 32 25" stroke="#7FFF00" strokeWidth="2"/>
                  <circle cx="20" cy="20" r="18" stroke="#7FFF00" strokeWidth="2" fill="none"/>
                </svg>
                <span className="logo-text">AEGIS</span>
              </div>
              <p className="footer-description">
                Empowering industrial designers with AI-driven stroke classification technology
              </p>
            </div>
          </div>

          <div className="footer-credits">
            <h4>Credits</h4>
            <div className="credit-links">
              <a href="https://www.linkedin.com/in/sankar4/?originalSubdomain=in" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="credit-link">
                <svg className="linkedin-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
                Sankar Balasubramanian
              </a>
              <a href="https://in.linkedin.com/in/amogh-setty-386698238" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="credit-link">
                <svg className="linkedin-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
                Amogh A S
              </a>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2025 AEGIS (AI Enhanced Gathering and Interpretation of Strokes). All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            
            <div className="modal-header">
              <div className="modal-icon">
                <svg viewBox="0 0 40 40" fill="none">
                  <path d="M5 20 Q 15 5, 25 20 T 35 20" stroke="#7FFF00" strokeWidth="3" fill="none"/>
                  <circle cx="20" cy="20" r="18" stroke="#7FFF00" strokeWidth="2" fill="none"/>
                </svg>
              </div>
              <h2>Welcome to AEGIS</h2>
              <p>Enter your username and password to continue</p>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={handleUsernameChange}
                  placeholder="Enter your username"
                  autoFocus
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter your password (min 6 characters)"
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <button type="submit" disabled={loading} className="modal-submit">
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    {username && password ? 'Logging in...' : 'Creating account...'}
                  </>
                ) : (
                  'Login / Sign Up'
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;

